import { z } from 'zod';
import { AllergenSchema } from './food.js';

// T-130: qty/uom NULLABLE. I tenant con `recipe_quantities_enabled=false`
// (default) gestiscono le ricette come anagrafica+allergeni senza dosi → qty/uom
// = null. I dati esistenti (numeri) restano validi.
export const RecipeIngredientSchema = z.object({
  food_id: z.string().uuid(),
  qty: z.number().nullable(),
  uom: z.string().max(10).nullable(),
  note: z.string().nullable().optional(),
});
export type RecipeIngredient = z.infer<typeof RecipeIngredientSchema>;

// T-259: ricetta base (semilavorato) usata come ingrediente, con quantità.
// Es. la ricetta "bottoni" usa 0.4 kg del semilavorato "ripieno bottoni".
// In produzione la qty scala con la resa (come un ingrediente) e il lotto del
// semilavorato prodotto viene scaricato/tracciato (source_production_id).
// T-130: qty/uom nullable (vedi sopra) per i tenant senza gestione quantità.
export const RecipeSubRecipeSchema = z.object({
  recipe_id: z.string().uuid(),
  qty: z.number().nullable(),
  uom: z.string().max(10).nullable(),
});
export type RecipeSubRecipe = z.infer<typeof RecipeSubRecipeSchema>;

// T-130: le quantità in ricetta sono obbligatorie SOLO se il tenant gestisce le
// quantità (`recipe_quantities_enabled`). Helper puro condiviso UI/test.
export function isQuantityRequired(recipeQuantitiesEnabled: boolean): boolean {
  return recipeQuantitiesEnabled;
}

export const RecipeProcessStepSchema = z.object({
  seq: z.number().int().nonnegative(),
  description: z.string().max(500),
  temperature_c: z.number().nullable().optional(),
  duration_min: z.number().int().nullable().optional(),
  is_critical_control_point: z.boolean().default(false),
});
export type RecipeProcessStep = z.infer<typeof RecipeProcessStepSchema>;

export const ChillingSignSchema = z.enum(['positive', 'negative']);
export type ChillingSign = z.infer<typeof ChillingSignSchema>;

// T-422 (CCP): tipo di controllo temperatura al cuore per la Scheda di produzione.
// - 'cottura' (DEFAULT): CCP di cottura ≥74 °C — comportamento storico, invariato.
//   La temperatura è richiesta e sotto 74 °C segnala non-conformità.
// - 'freddo': piatto servito freddo (es. insalata mista). La temperatura al cuore
//   è FACOLTATIVA, non blocca la chiusura e NON genera l'allarme "<74 °C". Se
//   inserita resta come dato ma non è un errore.
// Impostato dal consulente/owner sulla ricetta (classificazione HACCP), mai
// dall'operatore in cucina. Il default 'cottura' rende le ricette esistenti
// identiche a prima (fail-safe: nessun piatto che deve cuocere perde il gate).
export const CcpTypeSchema = z.enum(['cottura', 'freddo']);
export type CcpType = z.infer<typeof CcpTypeSchema>;
export const CCP_TYPE_LABELS: Record<CcpType, string> = {
  cottura: 'Cottura (≥ 74 °C al cuore)',
  freddo: 'Servito freddo (temperatura facoltativa)',
};

export const RecipeSchema = z
  .object({
    id: z.string().uuid(),
    tenant_id: z.string().uuid(),
    business_id: z.string().uuid().nullable(),
    name: z.string().min(1).max(200),
    description: z.string().nullable(),
    yield_qty: z.number(),
    yield_uom: z.string().max(10),
    // T-229 (collettiva): quante PORZIONI produce la ricetta base — in
    // preparazione si inserisce il n. di porzioni da produrre e gli
    // ingredienti scalano di porzioni/yield_portions (proporzione per
    // singola porzione, a prescindere dalla base con cui è stata scritta).
    yield_portions: z.number().int().positive().nullable().optional(),
    // T-229 (collettiva): menù di appartenenza (es. "A", "B"). Copiato su
    // productions.menu_label alla stampa, non si sceglie più in produzione.
    menu_label: z.string().max(40).nullable().optional(),
    // T-422: tipo di controllo temperatura al cuore (vedi CcpTypeSchema).
    // Default 'cottura' → ricette esistenti invariate.
    ccp_type: CcpTypeSchema.default('cottura'),
    ingredients: z.array(RecipeIngredientSchema).default([]),
    process_steps: z.array(RecipeProcessStepSchema).default([]),
    allergens: z.array(AllergenSchema).default([]),
    allergens_auto_computed: z.boolean().default(true),
    shelf_life_days: z.number().int().nullable(),
    chilling_enabled: z.boolean().default(false),
    chilling_minutes: z.number().int().min(0).max(1440).nullable().default(null),
    chilling_sign: ChillingSignSchema.nullable().default(null),
    temp_start_c: z.number().nullable().default(null),
    temp_end_c: z.number().nullable().default(null),
    freezing_enabled: z.boolean().default(false),
    default_shelf_life_minutes: z.number().int().positive().nullable().default(null),
    // Validità operative distinte dalla shelf life della preparazione:
    // solo una viene proposta in UI in base a `freezing_enabled`, ma entrambe
    // restano memorizzate per non perdere il valore quando il flag cambia.
    shelf_life_minutes_opened: z.number().int().positive().nullable().default(null),
    shelf_life_minutes_thawed: z.number().int().positive().nullable().default(null),
    // Lista id delle ricette base (semilavorati). Mantenuta sincronizzata con
    // `parent_recipes` per retro-compat: propagazione allergeni + ricette legacy.
    parent_recipe_ids: z.array(z.string().uuid()).default([]),
    // T-259: ricette base con quantità. Sorgente di verità per lo scaling e la
    // tracciabilità in produzione; `parent_recipe_ids` ne è la proiezione id.
    parent_recipes: z.array(RecipeSubRecipeSchema).default([]),
    is_favorite: z.boolean().default(false),
    // T-337 (Food Cost): true = ricetta di SOLO calcolo costo. Nascosta dai
    // picker operativi (stampa rapida / preparazione) per tutti i ruoli e
    // gestita solo da ristoratore/consulente/owner, ma resta nel modulo Food
    // Cost (non si archivia). Default false → ricette esistenti invariate.
    is_food_cost_only: z.boolean().default(false),
    // T-287 (Food Cost): prezzo di vendita (IVA inclusa) in centesimi, per il
    // calcolo del food cost % e del margine. Null = non impostato.
    selling_price_cents: z.number().int().nonnegative().nullable().default(null),
    // T-450 F1: aliquota IVA per questo piatto (override). null = usa il default
    // del locale (tenants.settings.default_vat_rate, o 10% se assente). % intera.
    vat_rate: z.number().nonnegative().nullable().default(null),
    // T-441 (varianti dieta collettiva): una variante è una ricetta FIGLIA. Base
    // = entrambi null (come oggi). Una variante ha variant_of_recipe_id (id della
    // base) + diet_code (codice collettivaDiet). Modello a 1 livello (una base non
    // è mai variante di un'altra); isolamento same-tenant garantito dal trigger DB.
    variant_of_recipe_id: z.string().uuid().nullable().default(null),
    diet_code: z.string().max(60).nullable().default(null),
    // Fingerprint degli ingredienti della base al momento del fork/ultimo
    // allineamento della variante → rileva la deriva (base modificata dopo la
    // variante) senza auto-propagare. Vedi spec §5.4.
    variant_base_fingerprint: z.string().max(64).nullable().default(null),
    // T-459: path bucket tech-sheets della scheda tecnica (PDF). null se assente.
    data_sheet_file_id: z.string().nullable().optional(),
    created_at: z.string().datetime(),
    archived_at: z.string().datetime().nullable(),
    version: z.number().int().nonnegative().default(0),
  })
  .refine(
    (r) =>
      r.chilling_enabled
        ? r.chilling_minutes !== null && r.chilling_sign !== null
        : r.chilling_minutes === null && r.chilling_sign === null,
    { message: 'chilling_minutes/sign coerenti con chilling_enabled' },
  );
export type Recipe = z.infer<typeof RecipeSchema>;
